import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { useSiteConfig, pickI18n } from '../contexts/SiteConfigContext';
import PageBanner from '../components/PageBanner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const TIMES = ['09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00'];

const BookingPage = () => {
  const { lang } = useLang();
  const { config } = useSiteConfig();
  const navigate = useNavigate();
  const colors = config.identity?.colors || {};
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', service: '', date: '', time: '', notes: '',
  });
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const labels = {
    de: { title: 'Termin buchen', subtitle: 'Wählen Sie Ihren Wunschservice und Termin', name: 'Name', phone: 'Telefon', email: 'E-Mail', service: 'Service auswählen', date: 'Datum', time: 'Uhrzeit', confirm: 'Termin bestätigen', success: 'Ihr Termin wurde erfolgreich angefragt!', back: 'Zur Startseite' },
    en: { title: 'Book Appointment', subtitle: 'Choose your service and preferred time', name: 'Name', phone: 'Phone', email: 'Email', service: 'Select Service', date: 'Date', time: 'Time', confirm: 'Confirm Booking', success: 'Your appointment has been requested!', back: 'Back to Home' },
    ar: { title: 'حجز موعد', subtitle: 'اختاري الخدمة والموعد المفضل', name: 'الاسم', phone: 'الهاتف', email: 'البريد الإلكتروني', service: 'اختاري الخدمة', date: 'التاريخ', time: 'الوقت', confirm: 'تأكيد الحجز', success: 'تم طلب موعدك بنجاح!', back: 'العودة للرئيسية' },
  };
  const L = labels[lang] || labels.de;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/bookings', form);
      toast.success(L.success);
      setSubmitted(true);
    } catch {
      toast.error('Error');
    }
  };

  if (submitted) {
    return (
      <>
        <PageBanner tag={L.title} title={L.success} />
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div
              className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: colors.bg_light || '#fdf2f5' }}
            >
              <CheckCircle2 className="w-10 h-10" strokeWidth={1.5} style={{ color: colors.primary }} />
            </div>
            <h2 className="font-display text-2xl md:text-3xl mb-4" style={{ color: colors.text_dark, fontWeight: 500 }}>
              {form.name ? `${form.name},` : ''} {L.success}
            </h2>
            <Button
              onClick={() => navigate('/')}
              className="btn-rose text-white rounded-full px-8 h-12 font-medium"
              style={{ backgroundColor: colors.primary }}
            >
              {L.back}
            </Button>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageBanner tag={L.title} title={L.title} subtitle={L.subtitle} />
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <form
            onSubmit={submit}
            className="bg-white rounded-2xl p-8 md:p-12 space-y-6"
            style={{ boxShadow: '0 1px 3px rgba(196, 71, 122, 0.06), 0 8px 24px -10px rgba(196, 71, 122, 0.12)' }}
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className="mb-2 block text-sm" style={{ color: colors.text_dark }}>{L.name}</Label>
                <Input required value={form.name} onChange={onChange('name')} className="h-11" />
              </div>
              <div>
                <Label className="mb-2 block text-sm" style={{ color: colors.text_dark }}>{L.phone}</Label>
                <Input required value={form.phone} onChange={onChange('phone')} className="h-11" />
              </div>
            </div>
            <div>
              <Label className="mb-2 block text-sm" style={{ color: colors.text_dark }}>{L.email}</Label>
              <Input type="email" value={form.email} onChange={onChange('email')} className="h-11" />
            </div>
            <div>
              <Label className="mb-2 block text-sm" style={{ color: colors.text_dark }}>{L.service}</Label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder={L.service} />
                </SelectTrigger>
                <SelectContent>
                  {(config.services || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{pickI18n(s.name, lang)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <Label className="mb-2 block text-sm" style={{ color: colors.text_dark }}>{L.date}</Label>
                <Input type="date" required value={form.date} onChange={onChange('date')} className="h-11" />
              </div>
              <div>
                <Label className="mb-2 block text-sm" style={{ color: colors.text_dark }}>{L.time}</Label>
                <Select value={form.time} onValueChange={(v) => setForm((f) => ({ ...f, time: v }))}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="--:--" /></SelectTrigger>
                  <SelectContent>
                    {TIMES.map((tm) => <SelectItem key={tm} value={tm}>{tm}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              type="submit"
              size="lg"
              className="w-full btn-rose text-white rounded-full h-13 px-8 gap-2 font-medium text-base"
              style={{ backgroundColor: colors.primary }}
            >
              <Calendar className="w-4 h-4" />
              {L.confirm}
            </Button>
          </form>
        </div>
      </section>
    </>
  );
};

export default BookingPage;
